import XcCallToAction from "@electricMotorShop/Components/XcCallToAction";
import XcProductPartNumbers from "@electricMotorShop/Components/XcProductPartNumbers";
import { emsBackgroundYellow, emsMediumGrey, emsWarningYellow } from "@electricMotorShop/EmsVariables";
import EMSAddToList from "@electricMotorShop/Icons/EMSAddToList";
import ProductAvailability, {
    ProductAvailabilityStyles,
} from "@electricMotorShop/Overrides/Components/ProductAvailability";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getErrorMessages } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { ProductSettingsModel, PromotionModel, WarehouseDto } from "@insite/client-framework/Types/ApiModels";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import ProductBinNumbers from "@insite/content-library/Components/ProductBinNumbers";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import CartLineNotes, { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import XCircle from "@insite/mobius/Icons/XCircle";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { SelectPresentationProps } from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useContext } from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// XCentium override for part numbers and availability
// KK - 2/29/24 - added XcCallToAction

interface OwnProps {
    cart: Cart;
    promotions: PromotionModel[];
    productSettings: ProductSettingsModel;
    showInventoryAvailability?: boolean;
    showLineNotes?: boolean;
    showImage?: boolean;
    showProductTitle?: boolean;
    showBrand?: boolean;
    showPerUnitPrice?: boolean;
    showPriceLabel?: boolean;
    showAddToList?: boolean;
    showProductNumber?: boolean;
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine: HandleThunkActionCreator<typeof removeCartLine>;
    showRemoveAction?: boolean;
    hideAddToList?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
    showVmiBinDetails?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    userName: state.context.session.userName,
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

type Props = OwnProps &
    HasCartLineContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CartLineCardExpandedStyles {
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    cartLineInfoGridItem?: GridItemProps;
    cartLineInfoContainer?: GridContainerProps;
    productInfoGridItem?: GridItemProps;
    productInfoContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: GridItemProps;
    configurationOptionText?: TypographyProps;
    productPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    quantityAndExtendedUnitNetPriceGridItem?: GridItemProps;
    quantityAndExtendedUnitNetPriceContainer?: GridContainerProps;
    quantityGridItem?: GridItemProps;
    quantity?: CartLineQuantityStyles;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPriceText?: TypographyProps;
    vatLabelText?: TypographyPresentationProps;
    subtotalWithoutVatText?: TypographyPresentationProps;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyProps;
    cartLineMessagesWrapper?: InjectableCss;
    warningIcon?: IconPresentationProps;
    productPriceAndAvailabilityGridItem?: GridItemProps;
    productPrice?: ProductPriceStyles;
    promotionNameText?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    addToListLink?: LinkPresentationProps;
    deliveryScheduleButton?: ButtonPresentationProps;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeSelect?: SelectPresentationProps;
    costCodeText?: TypographyPresentationProps;
    cartLineNotesGridItem?: GridItemProps;
    cartLineNotes?: CartLineNotesStyles;
    removeCartLineGridItem?: GridItemProps;
    removeCartLineIcon?: IconPresentationProps;
    binInformationsGridItem?: GridItemProps;
    productBinNumbers?: ProductPartNumbersStyles;
    cartLineMessages?: GridItemProps;
    cartLineMessagesText?: TypographyProps;
    productErpNumberText?: TypographyProps;
    productPriceAndQuantityGridItem?: GridItemProps;
    productBinNumberGridItem?: GridItemProps;
    addToListGridItem?: GridItemProps;
    productPriceAndQuantityContainer?: GridContainerProps;
    addToListContainer?: InjectableCss;
    addToListIcon?: IconPresentationProps;
    lineNotesWrapper?: InjectableCss;
}

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    addToListContainer: { css:css`display: flex;` },
    cartLineNotesGridItem: { width: 12 },
    container: {
        gap: 10,
        css: css`
            border-bottom: 1px solid ${emsMediumGrey}};
            padding-top: 1rem;
        `,
    },
    productImageGridItem: {
        width: 2,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    null,
                ])}
        `,
    },
    cartLineInfoGridItem: { width: 10 },
    productBrandAndDescriptionGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            flex-direction: column;
        `,
    },
    productErpNumberText: {
        css: css`
            width: 100%;
            word-wrap: break-word;
        `,
    },
    productPriceAndQuantityGridItem: { width: [12, 12, 12, 7, 7] },
    productPriceAndAvailabilityGridItem: {
        width: [7, 7, 7, 7, 8],
        css: css`
            flex-direction: column;
        `,
    },
    cartLineErrorMessageText: { color: "danger" },
    cartLineMessagesWrapper: {
        css: css`
        display: flex;
        align-items: center;
        border: 2px solid ${emsWarningYellow};
        border-radius: 4px;
        background: ${emsBackgroundYellow};
        padding: 15px 10px;
        margin-bottom: 5px;
        `,
    },
    warningIcon: {
        src: "AlertCircle",
        color: emsWarningYellow,
        css: css`
            margin-right: 10px;
            padding-bottom: 2px;
        `,
    },
    quantityGridItem: {
        width: [5, 5, 5, 5, 4],
        css: css`
            flex-direction: column;
        `,
    },
    addToListGridItem: {
        width: 12,
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: start;
            padding-top: 11px;
        `,
    },
    removeCartLineGridItem: {
        width: [2, 2, 2, 1, 1],
        css: css`
            justify-content: center;
        `,
    },
    removeCartLineIcon: { src: XCircle },
    productBinNumberGridItem: {
        width: [12, 12, 12, 3, 3],
        css: css`
            flex-direction: column;
        `,
    },
    cartLineMessages: {
        width: 12,
    },
    cartLineMessagesText: {},
    lineNotesWrapper: {
        css: css`
            padding-top: 0px;
        `,
    },
    addToListLink: {
        css: css`
            font-size: 14px;
            font-weight: 600;
        `,
    },
    addToListIcon: {
        src: EMSAddToList,
        css: css`
            margin: 5px;
            color: ${getColor("primary.main")};
        `,
    },
};

const CartLineCardExpanded: FC<Props> = ({
    cart,
    cartLine,
    showInventoryAvailability,
    showLineNotes,
    showAddToList = true,
    updateCartLine,
    removeCartLine,
    extendedStyles,
    showRemoveAction,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    hideAddToList,
    showVmiBinDetails,
    userName,
}) => {
    const toasterContext = useContext(ToasterContext);
    const currentMode = getCookie(NavigationModeCookieName);

    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (qtyOrdered !== cartLine.qtyOrdered) {
            if (qtyOrdered <= 0) {
                removeCartLine({ cartLineId: cartLine.id });
            } else {
                updateCartLine({
                    cartLine: {
                        ...cartLine,
                        qtyOrdered,
                    },
                });
            }
        }
    };

    const notesChangeHandler = (notes: string) => {
        if (notes !== cartLine.notes) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    notes,
                },
                shouldNotReloadPromotions: true,
                shouldNotLoadFullCart: true,
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Line Notes Updated"),
                        messageType: "success",
                    });
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        this.onSuccess?.();
                    }
                },
            });
        }
    };

    const removeCartLineClickHandler = () => {
        if (!showRemoveAction) {
            return;
        }

        removeCartLine({ cartLineId: cartLine.id });
    };

    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: cartLine.productId!,
            qtyOrdered: cartLine.qtyOrdered!,
            unitOfMeasure: cartLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const errorMessages = getErrorMessages(cart, cartLine, showInventoryAvailability);
    const [styles] = React.useState(() => mergeToNew(cartLineCardExpandedStyles, extendedStyles));

    const populateWarehouseDto = (message: string) => {
        if (message) {
            const warehouseMessage = message.split("#");
            const warehouses: WarehouseDto[] = [];
            warehouseMessage.forEach(item => {
                const warehouse = {} as WarehouseDto;
                warehouse.description = item.split("|")[1];
                warehouse.qty = Number(item.split("|")[0]);
                warehouses.push(warehouse);
            });
            return warehouses;
        }
        return undefined;
    };

    const warehouses = populateWarehouseDto(cartLine.properties["warehouses"]);

    return (
        <GridContainer
            {...styles.container}
            data-test-selector={`cartline_expanded_${cartLine.productId}_${cartLine.unitOfMeasure}`}
        >
            <GridItem {...styles.productImageGridItem}>
                <ProductImage product={cartLine} extendedStyles={styles.productImage} />
            </GridItem>
            <GridItem {...styles.cartLineInfoGridItem}>
                <GridContainer {...styles.cartLineInfoContainer}>
                    <GridItem {...styles.productBrandAndDescriptionGridItem}>
                        {cartLine.brand && <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />}
                        <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                        <XcProductPartNumbers
                            modelNumber={cartLine.manufacturerItem}
                            itemNumber={cartLine.erpNumber}
                            customerProductNumber={cartLine.customerName}
                            extendedStyles={styles.productPartNumbers}
                        />
                        {/* Comment out - Requested by Justin - Opti-203
                        {!userName ? (
                            <XcCallToAction/>
                        ) : (null) */} 
                        
                    </GridItem>

                    {showVmiBinDetails && currentMode === "Vmi" && (
                        <GridItem {...styles.binInformationsGridItem}>
                            <ProductBinNumbers
                                locationId={cart.vmiLocationId}
                                binId={cartLine.vmiBinId}
                                isCondensed={false}
                                extendedStyles={styles.productBinNumbers}
                            />
                        </GridItem>
                    )}

                    <GridItem {...styles.productPriceAndQuantityGridItem}>
                        <GridContainer {...styles.productPriceAndQuantityContainer}>
                            <GridItem {...styles.productPriceAndAvailabilityGridItem}>
                                {!cart.cartNotPriced && (
                                    <ProductPrice
                                        product={cartLine}
                                        currencySymbol={cart.currencySymbol}
                                        showSavings={false}
                                        showInvalidPriceMessage={true}
                                        extendedStyles={styles.productPrice}
                                    />
                                )}
                                {showInventoryAvailability && !cartLine.quoteRequired && (
                                    <ProductAvailability
                                        productId={cartLine.productId!}
                                        availability={cartLine.availability!}
                                        unitOfMeasure={cartLine.unitOfMeasure}
                                        trackInventory={cartLine.trackInventory}
                                        failedToLoadInventory={cart.failedToGetRealTimeInventory}
                                        extendedStyles={styles.productAvailability}
                                        infoWarehouses={warehouses}
                                    />
                                )}

                                {errorMessages.length > 0 &&
                                    errorMessages.map((message, index) => (
                                        <Typography
                                            {...styles.cartLineErrorMessageText}
                                            key={index}
                                            data-test-selector="cartline_errorMessage"
                                        >
                                            {message}
                                        </Typography>
                                    ))}
                            </GridItem>

                            <GridItem {...styles.quantityGridItem}>
                                <GridContainer>
                                    <GridItem width={9}>
                                        <CartLineQuantity
                                            cart={cart}
                                            editable={true}
                                            onQtyOrderedChange={qtyOrderedChangeHandler}
                                            extendedStyles={styles.quantity}
                                        />
                                    </GridItem>

                                    <GridItem {...styles.removeCartLineGridItem}>
                                        {showRemoveAction && (
                                            <Clickable
                                                onClick={removeCartLineClickHandler}
                                                data-test-selector="cartline_removeLine"
                                            >
                                                <VisuallyHidden>{translate("Remove item")}</VisuallyHidden>
                                                <IconMemo {...styles.removeCartLineIcon} />
                                            </Clickable>
                                        )}
                                    </GridItem>
                                    <GridItem {...styles.addToListGridItem} data-test-selector="add-to-list-grid-item">
                                        {showAddToList &&
                                            !hideAddToList &&
                                            cartLine.canAddToWishlist &&
                                            !cartLine.isRestricted && (
                                                <StyledWrapper {...styles.addToListContainer}>
                                                    <Clickable
                                                        onClick={addToListClickHandler}
                                                        data-test-selector="add-to-list-icon"
                                                    >
                                                        <IconMemo {...styles.addToListIcon} />
                                                    </Clickable>
                                                    <Link {...styles.addToListLink} onClick={addToListClickHandler}>
                                                        {translate("Add to List")}
                                                    </Link>

                                                </StyledWrapper>
                                            )}
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                        </GridContainer>
                        {/* <GridItem {...styles.addToListGridItem}>
                        
                        </GridItem> */}
                    </GridItem>
                </GridContainer>
            </GridItem>
            {cartLine.properties["messages"] && (
                <GridItem {...styles.cartLineMessages}>
                    <StyledWrapper {...styles.cartLineMessagesWrapper}>
                        <Icon {...styles.warningIcon} />
                        <Typography {...styles.cartLineMessagesText}>{cartLine.properties["messages"]}</Typography>
                    </StyledWrapper>
                </GridItem>
            )}
            <GridItem width={12}>
                {showLineNotes && !cartLine.isPromotionItem && cart.properties["isPunchout"] === undefined && (
                    <CartLineNotes
                        cart={cart}
                        editable={true}
                        onNotesChange={notesChangeHandler}
                        extendedStyles={styles.cartLineNotes}
                    />
                )}
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(CartLineCardExpanded));
